import { APP_PATHS } from '@/config/routes/app-paths';
import { getWorkspaceRole } from '@/utils/workspace-role';
import { isGlobalAdmin } from '@/utils/is-global-admin';
import { ROLES } from '@/types/auth';
import type { Admin, RoleType } from '@/types/auth';

export const DASHBOARD_NAV_PATHS = [
  APP_PATHS.overview,
  APP_PATHS.billing,
  APP_PATHS.services,
  APP_PATHS.wholesalerSettings,
  APP_PATHS.team,
  APP_PATHS.adminSettings,
] as const;

export const ADMIN_WITHOUT_WHOLESALER_ALLOWED_PATHS = [APP_PATHS.overview] as const;

export type DashboardNavPath = (typeof DASHBOARD_NAV_PATHS)[number];

const { globalAdmin, admin, billingAdmin } = ROLES;

/**
 * Which roles may open each dashboard page. Admin Settings is Global-Admin-only; Billing Admin
 * is scoped to Overview + Billing; Admin sees everything except Admin Settings.
 * Client-side gating is UX-only — the server enforces the same rules.
 */
export const DASHBOARD_PATH_ALLOWED_ROLES: Record<DashboardNavPath, readonly RoleType[]> = {
  [APP_PATHS.overview]: [admin, billingAdmin, globalAdmin],
  [APP_PATHS.billing]: [admin, billingAdmin, globalAdmin],
  [APP_PATHS.services]: [admin, globalAdmin],
  [APP_PATHS.wholesalerSettings]: [admin, globalAdmin],
  [APP_PATHS.team]: [admin, globalAdmin],
  [APP_PATHS.adminSettings]: [globalAdmin],
};

/**
 * The public shell: landing plus the passwordless entry flows. Everything else renders
 * inside `DashboardRouteLayout`, so the check below is written as "not public" rather than
 * as a list of dashboard paths — a new dashboard route then gets the right loading shell
 * without anyone having to remember to register it here.
 */
const PUBLIC_SHELL_PATHS = [
  APP_PATHS.home,
  APP_PATHS.activate,
  APP_PATHS.signin,
  APP_PATHS.pendingActivation,
  APP_PATHS.recoverAccount,
] as const;

export const isDashboardShellPath = (pathname: string): boolean => {
  return !PUBLIC_SHELL_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));
};

export const isDashboardNavPathAccessible = (
  pathname: string,
  currentUser: Admin | null,
  selectedWholesalerId: string | null,
): boolean => {
  const navPath = DASHBOARD_NAV_PATHS.find(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );

  if (navPath == null) {
    return true;
  }

  // Admin Settings is decided before every other rule — including the no-user and
  // no-wholesaler branches below, since Global Admin is platform-wide, not workspace-scoped.
  // This is a deny-by-default gate: anything that isn't a confirmed Global Admin is refused.
  if (navPath === APP_PATHS.adminSettings) {
    return isGlobalAdmin(currentUser, selectedWholesalerId);
  }

  if (currentUser == null) {
    return true;
  }

  if (currentUser.wholesalers.length === 0) {
    return ADMIN_WITHOUT_WHOLESALER_ALLOWED_PATHS.some(
      (path) => pathname === path || pathname.startsWith(`${path}/`),
    );
  }

  const workspaceRole = getWorkspaceRole(currentUser, selectedWholesalerId);

  // Unrecognised backend role string — don't lock the UI over it; the server enforces access.
  if (workspaceRole == null) {
    return true;
  }

  return DASHBOARD_PATH_ALLOWED_ROLES[navPath].includes(workspaceRole);
};
